// Neon 8-Ball Pool Server Engine (2 Players + Spectators)
export class PoolEngine {
  constructor(io, room) {
    this.io = io;
    this.room = room;
    this.tableW = 800;
    this.tableH = 440;
    this.ballR = 11;
    this.balls = [];
    this.players = [];
    this.currentTurnIdx = 0;
    this.suitAssigned = false;
    this.playerSuits = {}; // id -> 'solids' | 'stripes'
    this.pockets = [
      { x: 30, y: 30, r: 24 }, { x: 400, y: 22, r: 22 }, { x: 770, y: 30, r: 24 },
      { x: 30, y: 410, r: 24 }, { x: 400, y: 418, r: 22 }, { x: 770, y: 410, r: 24 }
    ];
    this.isMoving = false;
    this.loopInterval = null;
  }

  start() {
    const shouldFillBots = this.room.settings?.fillBots !== false;
    this.players = this.room.players.slice(0, 2).map(p => ({ id: p.id, name: p.name, isBot: !!p.isBot }));

    if (this.players.length === 1 && shouldFillBots) {
      this.players.push({ id: 'bot_pool', name: 'Shark Bot 🎱', isBot: true });
    }

    this.currentTurnIdx = 0;
    this.suitAssigned = false;
    this.playerSuits = {};

    this.initBalls();

    this.io.to(this.room.code).emit('pool_started', {
      players: this.players,
      currentTurnId: this.players[this.currentTurnIdx]?.id,
      balls: this.balls,
      playerSuits: this.playerSuits
    });

    if (this.loopInterval) clearInterval(this.loopInterval);
    this.loopInterval = setInterval(() => this.physicsStep(), 1000 / 60);

    this.checkBotTurn();
  }

  checkBotTurn() {
    const cp = this.players[this.currentTurnIdx];
    if (!cp || !cp.isBot || this.isMoving) return;

    setTimeout(() => {
      if (this.isMoving) return;
      const cue = this.balls[0];
      if (!cue || cue.inPocket) return;

      const mySuit = this.playerSuits[cp.id];
      let targetBalls = [];
      if (mySuit) {
        targetBalls = this.balls.filter(b => !b.inPocket && b.type === mySuit);
      }
      if (targetBalls.length === 0) {
        targetBalls = this.balls.filter(b => !b.inPocket && (b.type === 'eight' || b.id > 0));
      }

      const target = targetBalls[Math.floor(Math.random() * targetBalls.length)] || this.balls[1];
      if (target) {
        const angle = Math.atan2(target.y - cue.y, target.x - cue.x) + (Math.random() - 0.5) * 0.08;
        const power = 10 + Math.random() * 8;
        this.shoot(cp.id, angle, power);
      }
    }, 1200);
  }

  initBalls() {
    this.balls = [];
    // Cue Ball (0 = White)
    this.balls.push({ id: 0, num: 0, type: 'cue', x: 220, y: 220, vx: 0, vy: 0, inPocket: false, color: '#ffffff' });

    // Triangle Rack on right side
    const startX = 560;
    const startY = 220;
    const colors = [
      '#eab308', '#38bdf8', '#ef4444', '#a855f7', '#f97316', '#22c55e', '#ec4899', // 1-7 Solids
      '#1e293b', // 8 Black
      '#eab308', '#38bdf8', '#ef4444', '#a855f7', '#f97316', '#22c55e', '#ec4899'  // 9-15 Stripes
    ];

    const rackTypes = [
      'solid',
      'stripe', 'solid',
      'solid', 'eight', 'stripe',
      'stripe', 'solid', 'stripe', 'solid',
      'solid', 'stripe', 'stripe', 'solid', 'stripe'
    ];

    let ballIdx = 1;
    let row = 0;
    let col = 0;
    for (let r = 0; r < 5; r++) {
      for (let c = 0; c <= r; c++) {
        const type = rackTypes[ballIdx - 1];
        const num = type === 'eight' ? 8 : (ballIdx <= 7 ? ballIdx : ballIdx);
        this.balls.push({
          id: ballIdx,
          num: ballIdx,
          type: type === 'eight' ? 'eight' : (ballIdx <= 7 ? 'solid' : 'stripe'),
          x: startX + r * (this.ballR * 1.8),
          y: startY + (c - r / 2) * (this.ballR * 2.1),
          vx: 0,
          vy: 0,
          inPocket: false,
          color: colors[ballIdx - 1]
        });
        ballIdx++;
      }
    }
  }

  checkBotTurn() {
    const cp = this.players[this.currentTurnIdx];
    if (cp && cp.isBot && !this.isMoving) {
      setTimeout(() => {
        if (!this.isMoving) {
          const cue = this.balls[0];
          const targets = this.balls.filter(b => !b.inPocket && b.id !== 0);
          if (targets.length > 0) {
            const target = targets[Math.floor(Math.random() * targets.length)];
            const angle = Math.atan2(target.y - cue.y, target.x - cue.x);
            this.shoot(cp.id, angle, 12 + Math.random() * 8);
          }
        }
      }, 1200);
    }
  }

  shoot(playerId, angle, power) {
    const cp = this.players[this.currentTurnIdx];
    if (!cp || cp.id !== playerId || this.isMoving) return;

    const cue = this.balls[0];
    if (cue.inPocket) return;

    const clampedPower = Math.min(24, Math.max(2, power));
    cue.vx = Math.cos(angle) * clampedPower;
    cue.vy = Math.sin(angle) * clampedPower;
    this.isMoving = true;

    this.io.to(this.room.code).emit('pool_shotTaken', {
      playerId,
      angle,
      power: clampedPower
    });
  }

  physicsStep() {
    let anyMoving = false;

    // Movement & Wall Bounce
    for (const b of this.balls) {
      if (b.inPocket) continue;

      b.x += b.vx;
      b.y += b.vy;

      // Friction
      b.vx *= 0.985;
      b.vy *= 0.985;

      if (Math.abs(b.vx) < 0.05) b.vx = 0;
      if (Math.abs(b.vy) < 0.05) b.vy = 0;

      if (b.vx !== 0 || b.vy !== 0) anyMoving = true;

      // Table Cushion Bounce
      const minX = 35 + this.ballR;
      const maxX = this.tableW - 35 - this.ballR;
      const minY = 35 + this.ballR;
      const maxY = this.tableH - 35 - this.ballR;

      if (b.x < minX) { b.x = minX; b.vx = -b.vx * 0.85; }
      if (b.x > maxX) { b.x = maxX; b.vx = -b.vx * 0.85; }
      if (b.y < minY) { b.y = minY; b.vy = -b.vy * 0.85; }
      if (b.y > maxY) { b.y = maxY; b.vy = -b.vy * 0.85; }

      // Pocket Check
      for (const p of this.pockets) {
        if (Math.hypot(b.x - p.x, b.y - p.y) < p.r) {
          b.inPocket = true;
          b.vx = 0;
          b.vy = 0;
          this.handlePocket(b);
          break;
        }
      }
    }

    // Ball-to-Ball Collisions
    for (let i = 0; i < this.balls.length; i++) {
      const b1 = this.balls[i];
      if (b1.inPocket) continue;

      for (let j = i + 1; j < this.balls.length; j++) {
        const b2 = this.balls[j];
        if (b2.inPocket) continue;

        const dx = b2.x - b1.x;
        const dy = b2.y - b1.y;
        const dist = Math.hypot(dx, dy);

        if (dist < this.ballR * 2 && dist > 0) {
          // Normal & Tangent vectors
          const nx = dx / dist;
          const ny = dy / dist;

          // Overlap resolution
          const overlap = (this.ballR * 2 - dist) / 2;
          b1.x -= nx * overlap;
          b1.y -= ny * overlap;
          b2.x += nx * overlap;
          b2.y += ny * overlap;

          // Elastic collision response
          const kx = b1.vx - b2.vx;
          const ky = b1.vy - b2.vy;
          const p = 2 * (nx * kx + ny * ky) / 2;

          b1.vx -= p * nx * 0.95;
          b1.vy -= p * ny * 0.95;
          b2.vx += p * nx * 0.95;
          b2.vy += p * ny * 0.95;

          anyMoving = true;
        }
      }
    }

    this.io.to(this.room.code).emit('pool_state', {
      balls: this.balls,
      isMoving: anyMoving
    });

    if (this.isMoving && !anyMoving) {
      this.isMoving = false;
      this.finishTurn();
    }
  }

  handlePocket(b) {
    const cp = this.players[this.currentTurnIdx];

    if (b.id === 0) {
      // Scratch (cue ball pocketed): reset to table center
      setTimeout(() => {
        b.inPocket = false;
        b.x = 220;
        b.y = 220;
        b.vx = 0;
        b.vy = 0;
      }, 800);
      return;
    }

    if (b.type === 'eight') {
      // 8-Ball pocketed: Win if all suits pocketed, otherwise loss!
      const mySuit = this.playerSuits[cp.id];
      const remainingMySuit = this.balls.filter(x => !x.inPocket && x.type === mySuit).length;
      const winner = remainingMySuit === 0 ? cp : this.players.find(p => p.id !== cp.id);

      this.io.to(this.room.code).emit('pool_gameOver', { winner });
      return;
    }

    // Assign suits on first pocket
    if (!this.suitAssigned && (b.type === 'solid' || b.type === 'stripe')) {
      this.suitAssigned = true;
      this.playerSuits[cp.id] = b.type;
      const otherP = this.players.find(p => p.id !== cp.id);
      if (otherP) {
        this.playerSuits[otherP.id] = b.type === 'solid' ? 'stripe' : 'solid';
      }

      this.io.to(this.room.code).emit('pool_suitsAssigned', {
        playerSuits: this.playerSuits
      });
    }
  }

  finishTurn() {
    this.currentTurnIdx = (this.currentTurnIdx + 1) % this.players.length;
    this.io.to(this.room.code).emit('pool_turnChange', {
      currentTurnId: this.players[this.currentTurnIdx]?.id
    });
    this.checkBotTurn();
  }

  destroy() {
    if (this.loopInterval) clearInterval(this.loopInterval);
  }
}
